import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faTrash, faTruck } from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import CollapsibleCardV2 from "../basics/interface/CollapsibleCardV2"
import InputV2 from "../basics/input/InputV2"
import * as Addons from "../../utilities/Addons.js"

// Libellé court de la destination d'une livraison (POI sérialisé).
const livDestination = (liv) => {
	const po = liv.destination_adresse
	if (!po) return ""
	if (po.label) return po.label
	if (
		po.adresse &&
		`${po.adresse.rue || ""}${po.adresse.numero || ""}` !== ""
	)
		return `${po.adresse.rue} ${po.adresse.numero}`.trim()
	return ""
}

// Une restitution « existe » dès qu'elle porte une donnée (date posée par le
// flux Restituer, montant ou info saisis) — sinon la ligne reste masquée.
export const restitutionExiste = (dep) =>
	dep.restiDate !== "" ||
	(dep.restiInfo || "") !== "" ||
	parseFloat(dep.restiTot || 0) !== 0

// ── Le « document » : un décompte bordé dont la colonne des montants est une
// bande teintée continue, close par le double filet comptable au-dessus du
// total (double soulignement des totaux en compta). Chaque prestation est une
// ligne nom / détail / montant ; les notes sont des champs fantômes inline
// (classe .tarifs-note, états hover/focus dans DepFicheV2.css).
const doc = {
	border: `1px solid ${COLORS.border}`,
	borderRadius: 10,
	overflow: "hidden",
}
const GRID = "170px minmax(0, 1fr) 170px"
const row = {
	display: "grid",
	gridTemplateColumns: GRID,
	alignItems: "stretch",
}
// Hairline entre prestations — plus léger que COLORS.border, la structure
// forte reste la bande des montants.
const rowSuivante = { borderTop: "1px solid rgba(15, 23, 42, 0.05)" }
const cellNom = {
	display: "flex",
	alignItems: "center",
	gap: 8,
	padding: "10px 8px 10px 14px",
	fontSize: 13,
	fontWeight: 600,
	color: COLORS.text,
}
const cellDetail = {
	display: "flex",
	alignItems: "center",
	flexWrap: "wrap",
	gap: 8,
	padding: "7px 12px",
	minWidth: 0,
}
const cellMontant = {
	display: "flex",
	alignItems: "center",
	justifyContent: "flex-end",
	padding: "7px 12px",
	borderLeft: `1px solid ${COLORS.border}`,
	backgroundColor: COLORS.bg,
}
// En-tête de colonnes du décompte.
const entete = {
	...row,
	borderBottom: `1px solid ${COLORS.border}`,
	fontSize: 10,
	fontWeight: 700,
	letterSpacing: "0.08em",
	textTransform: "uppercase",
	color: COLORS.textLight,
}
const enteteCell = { padding: "8px 8px 8px 14px" }
// Montant calculé (lecture seule) : texte tabulaire, pas d'input mort.
// paddingRight = padding (11) + bordure (1) de gs.input, pour que les chiffres
// s'alignent sur ceux des montants éditables de la colonne.
const montantCalcule = (valeur) => ({
	fontSize: 13,
	fontVariantNumeric: "tabular-nums",
	paddingRight: 12,
	color: parseFloat(valeur || 0) ? COLORS.text : COLORS.textLight,
})
// Montant éditable : input compact aligné à droite dans la bande.
const montantInput = {
	...gs.input,
	width: 110,
	textAlign: "right",
	fontVariantNumeric: "tabular-nums",
	backgroundColor: COLORS.surface,
}
const qtInput = { ...gs.input, textAlign: "right", width: 80, flex: "none" }
const uniteSign = { color: COLORS.textLight, flexShrink: 0, fontSize: 12 }
// Note fantôme : discrète tant qu'on ne la survole/remplit pas.
const noteInput = {
	flex: 1,
	minWidth: 120,
	fontSize: 12,
	color: COLORS.textMuted,
	padding: "4px 8px",
	borderRadius: 6,
	border: "1px dashed transparent",
	backgroundColor: "transparent",
	fontFamily: "inherit",
}
const metaTexte = { fontSize: 12.5, color: COLORS.textMuted, minWidth: 0 }
// Ligne de total : double filet comptable sur la bande des montants.
const totalNomDetail = {
	display: "flex",
	alignItems: "center",
	justifyContent: "flex-end",
	padding: "12px",
	fontSize: 13,
	fontWeight: 700,
	color: COLORS.text,
	borderTop: `1px solid ${COLORS.border}`,
}
const totalMontant = {
	...cellMontant,
	borderTop: `1px solid ${COLORS.text}`,
	// paddingRight aligné sur l'axe des chiffres (cellule 12 + retrait
	// interne des inputs 12, cf. montantCalcule).
	padding: "12px 24px 12px 12px",
	fontSize: 17,
	fontWeight: 800,
	fontVariantNumeric: "tabular-nums",
	color: COLORS.text,
}

/**
 * Carte « Tarifs » : décompte façon document de facture. Colonne des montants
 * en bande teintée continue (éditable = input, calculé = texte), notes en
 * champs fantômes inline, total fermé par le double filet comptable. Les
 * livraisons (gérées par l'app mobile) et la restitution ne s'affichent que si
 * elles existent — sans restitution, l'action « Restituer » vit dans le footer
 * de la fiche (onRestituer ne sert ici qu'au bloc existant non daté).
 */
const DepFicheTarifsV2 = ({
	dep,
	updateDep,
	onFocusLost,
	gardButton,
	addAutre,
	updateAutre,
	removeAutre,
	onRestituer,
	refs,
	open,
	onToggle,
}) => {
	const livraisons = dep.livraisons || []
	return (
		<CollapsibleCardV2 title="Tarifs" open={open} onToggle={onToggle}>
			<div style={doc}>
				<div style={entete}>
					<span style={enteteCell}>Prestation</span>
					<span style={{ ...enteteCell, paddingLeft: 12 }}>
						Détail
					</span>
					<span
						style={{
							...cellMontant,
							padding: "8px 12px",
							fontSize: 10,
						}}
					>
						CHF
					</span>
				</div>

				{/* Tarif de base */}
				<div style={row}>
					<span style={cellNom}>Tarif de base</span>
					<span style={cellDetail} />
					<span style={cellMontant}>
						<InputV2
							style={montantInput}
							value={dep.baseTot}
							ref={refs.refTarifBase}
							onFocus={(e) => e.target.select()}
							onChange={(e) => updateDep("baseTot", e)}
							onBlur={(e) => onFocusLost("baseTot", e)}
							onKeyDown={(e) => Addons.typeNumber(e)}
							nextRef={refs.refNote}
						/>
					</span>
				</div>

				{/* Heures supplémentaires */}
				<div style={{ ...row, ...rowSuivante }}>
					<span style={cellNom}>Heures supp.</span>
					<span style={cellDetail}>
						<InputV2
							style={qtInput}
							value={dep.hSupQ}
							onFocus={(e) => e.target.select()}
							onChange={(e) => updateDep("hSupQ", e)}
							onBlur={(e) => onFocusLost("hSupQ", e)}
							onKeyDown={(e) => Addons.typeNumber(e)}
							nextRef={refs.refHsup}
							title="Nombre d'heures"
						/>
						<span style={uniteSign}>h ×</span>
						<InputV2
							style={qtInput}
							value={dep.hSupT}
							ref={refs.refHsup}
							onFocus={(e) => e.target.select()}
							onChange={(e) => updateDep("hSupT", e)}
							onBlur={(e) => onFocusLost("hSupT", e)}
							onKeyDown={(e) => Addons.typeNumber(e)}
							title="Tarif pour 1 heure"
						/>
						<span style={uniteSign}>CHF/h</span>
						<input
							className="tarifs-note"
							style={noteInput}
							value={dep.hSupInfo}
							placeholder="+ note"
							onFocus={(e) => e.target.select()}
							onChange={(e) => updateDep("hSupInfo", e)}
						/>
					</span>
					<span style={cellMontant}>
						<span style={montantCalcule(dep.hSupTot)}>
							{dep.hSupTot !== "" ? dep.hSupTot : "0"}
						</span>
					</span>
				</div>

				{/* Gardiennage */}
				<div style={{ ...row, ...rowSuivante }}>
					<span style={cellNom}>Gardiennage</span>
					<span style={cellDetail}>
						<InputV2
							style={qtInput}
							value={dep.gardQ}
							onFocus={(e) => e.target.select()}
							onChange={(e) => updateDep("gardQ", e)}
							onBlur={(e) => onFocusLost("gardQ", e)}
							onKeyDown={(e) => Addons.typeNumber(e)}
							title="Jours de gardiennage"
						/>
						<span style={uniteSign}>j ×</span>
						<InputV2
							style={qtInput}
							value={dep.gardT}
							onFocus={(e) => e.target.select()}
							onChange={(e) => updateDep("gardT", e)}
							onBlur={(e) => onFocusLost("gardT", e)}
							onKeyDown={(e) => Addons.typeNumber(e)}
							title="Tarif pour 1 jour"
						/>
						<span style={uniteSign}>CHF/j</span>
						<button
							type="button"
							style={{
								...gs.btnSecondary,
								padding: "5px 10px",
								fontSize: 12,
							}}
							onClick={gardButton}
							title="Calculer les jours de gardiennage (passages au parc)"
						>
							Calculer
						</button>
						<input
							className="tarifs-note"
							style={noteInput}
							value={dep.gardInfo}
							placeholder="+ note"
							onFocus={(e) => e.target.select()}
							onChange={(e) => updateDep("gardInfo", e)}
						/>
					</span>
					<span style={cellMontant}>
						<span style={montantCalcule(dep.gardTot)}>
							{dep.gardTot !== "" ? dep.gardTot : "0"}
						</span>
					</span>
				</div>

				{/* GE-67 (Jordan 10.10, Q7) : plus de fournitures sur la fiche
				    dépannage — elles vivent sur la fiche atelier « client de
				    passage » liée (bouton dans l'en-tête de la fiche). */}
				{/* Livraisons — lecture seule (gérées par l'app mobile),
				    comptées dans le total. Affichées seulement s'il y en a. */}
				{livraisons.map((liv, index) => (
					<div
						key={`liv_${liv.id}`}
						style={{ ...row, ...rowSuivante }}
					>
						<span style={cellNom}>
							<FontAwesomeIcon
								icon={faTruck}
								style={{
									fontSize: 11,
									color: COLORS.textLight,
								}}
							/>
							Livraison {index + 1}
						</span>
						<span
							style={cellDetail}
							title="Livraison gérée par l'app mobile"
						>
							<span style={metaTexte}>
								{[
									[liv.date, liv.heure]
										.filter(Boolean)
										.join(" "),
									livDestination(liv),
								]
									.filter(Boolean)
									.join(" — ") || "—"}
							</span>
						</span>
						<span style={cellMontant}>
							<span style={montantCalcule(liv.tot)}>
								{liv.tot !== "" ? liv.tot : "—"}
							</span>
						</span>
					</div>
				))}

				{/* Restitution : affichée seulement si elle existe — l'action
				    « Restituer » vit dans le footer de la fiche. */}
				{dep.id > 0 && restitutionExiste(dep) && (
					<div style={{ ...row, ...rowSuivante }}>
						<span style={cellNom}>Restitution</span>
						<span style={cellDetail}>
							<input
								style={{
									...gs.input,
									width: 100,
									flex: "none",
								}}
								value={dep.restiDate}
								placeholder="Date"
								onFocus={(e) => e.target.select()}
								onChange={(e) => updateDep("restiDate", e)}
								onKeyDown={(e) =>
									Addons.typeDate(
										e,
										null,
										updateDep,
										"restiDate",
									)
								}
							/>
							<input
								style={{
									...gs.input,
									width: 64,
									flex: "none",
								}}
								value={dep.restiHeure}
								placeholder="Heure"
								onFocus={(e) => e.target.select()}
								onChange={(e) => updateDep("restiHeure", e)}
								onKeyDown={(e) =>
									Addons.typeHeure(
										e,
										null,
										updateDep,
										"restiHeure",
									)
								}
							/>
							<input
								style={{ ...gs.input, width: 140 }}
								value={dep.restiReprisPar}
								placeholder="Repris par…"
								onFocus={(e) => e.target.select()}
								onChange={(e) => updateDep("restiReprisPar", e)}
							/>
							{dep.restiDate === "" && (
								<button
									type="button"
									style={{
										...gs.btnSecondary,
										padding: "5px 10px",
										fontSize: 12,
									}}
									onClick={onRestituer}
								>
									Restituer
								</button>
							)}
							<input
								className="tarifs-note"
								style={noteInput}
								value={dep.restiInfo}
								placeholder="+ note"
								onFocus={(e) => e.target.select()}
								onChange={(e) => updateDep("restiInfo", e)}
							/>
						</span>
						<span style={cellMontant}>
							<input
								style={montantInput}
								value={dep.restiTot}
								onFocus={(e) => e.target.select()}
								onChange={(e) => updateDep("restiTot", e)}
								onBlur={(e) => onFocusLost("restiTot", e)}
								onKeyDown={(e) => Addons.typeNumber(e)}
							/>
						</span>
					</div>
				)}

				{/* Autres prestations (lignes libres) */}
				{dep.autres.map((autre, index) => (
					<div
						key={`autre_${autre.id}`}
						style={{ ...row, ...rowSuivante }}
					>
						<span style={cellNom}>
							{index === 0 ? "Autres" : ""}
						</span>
						<span style={cellDetail}>
							<input
								style={{ ...gs.input, flex: 1 }}
								value={autre.info}
								placeholder="Libellé…"
								onFocus={(e) => e.target.select()}
								onChange={(e) => updateAutre("info", e, index)}
							/>
							{/* Suppression dès la 2e ligne — la première reste
							    la ligne « Autres » de base de la fiche. */}
							{index > 0 && (
								<button
									type="button"
									style={gs.iconBtn(COLORS.danger)}
									onClick={() => removeAutre(autre)}
									title="Supprimer la ligne"
									aria-label="Supprimer la ligne"
								>
									<FontAwesomeIcon icon={faTrash} />
								</button>
							)}
						</span>
						<span style={cellMontant}>
							<input
								style={montantInput}
								value={autre.total}
								onFocus={(e) => e.target.select()}
								onChange={(e) => updateAutre("total", e, index)}
								// GE-71 : « - » accepté (rabais).
								onKeyDown={(e) => Addons.typeNumberSigned(e)}
							/>
						</span>
					</div>
				))}
				<div style={{ ...row, ...rowSuivante }}>
					<span style={cellNom}>
						{dep.autres.length === 0 ? "Autres" : ""}
					</span>
					<span style={cellDetail}>
						<button
							type="button"
							style={gs.addBtn}
							onClick={addAutre}
						>
							<FontAwesomeIcon icon={faPlus} /> Ajouter une ligne
						</button>
					</span>
					<span style={cellMontant} />
				</div>

				{/* Total (rappel — la synthèse vit dans les métriques en haut
				    de page). Filet sur la bande des montants. */}
				<div style={row}>
					<span
						style={{
							...totalNomDetail,
							justifyContent: "flex-start",
							paddingLeft: 14,
						}}
					/>
					<span style={totalNomDetail}>Total final</span>
					<span style={totalMontant}>{dep.totalFinal}</span>
				</div>
			</div>
		</CollapsibleCardV2>
	)
}

export default DepFicheTarifsV2
